import Model, { attr, hasMany, belongsTo } from '@warp-drive/legacy/model';
import { isBlank } from '@ember/utils';
import { set } from '@ember/object';
import type AttachmentModel from './attachment.ts';
import type AssetSummaryModel from './asset-summary.ts';
import type { DocumentType } from '../types/tuition-assistance.ts';

/**
 * Validation states tracked on `digital_asset.tags.ai_validation_status`.
 * `pending` is set on upload; the others arrive via the AI agent ServiceResponse.
 */
export type AiValidationStatus = 'pending' | 'valid' | 'accepted' | 'flagged' | 'error';
export type AiValidationDisplayStatus = AiValidationStatus | 'not_validated';

interface DownloadUrlsSignature {
  original: string;
}

export default class DigitalAsset extends Model {
  @attr declare bucket: string;
  @attr declare contentType: string;
  @attr declare downloadUrls: DownloadUrlsSignature;
  @attr declare filename: string;
  @attr declare fullPath: string;
  @attr declare raw: Record<string, unknown>;
  @attr declare name: string;
  @attr declare path: string;
  @attr declare size: number;
  @attr declare slug: string;
  @attr declare tags: Record<string, unknown>;
  @attr declare uuid: string;
  @attr declare signedUrl: string;
  @attr declare classification: string;
  @attr declare description: string;

  @attr declare createdAt: string;
  @attr declare updatedAt: string;
  @attr declare attachmentDeletedAt: string | null;

  /*************************
   **  Relationships       **
   *************************/

  @hasMany('attachment', { async: false, inverse: null })
  declare attachments: AttachmentModel[];

  @belongsTo('asset-summary', {
    async: false,
    inverse: 'digitalAsset',
    polymorphic: true,
    as: 'digital-asset',
  })
  declare assetSummary: AssetSummaryModel | null;

  /*************************
   **  Computed Properties **
   *************************/

  get presentableName() {
    const { name, filename } = this;

    return isBlank(name) ? filename : name;
  }

  set presentableName(value) {
    set(this, 'name', value);
  }

  // TODO: convert
  get assetUrl() {
    return this.downloadUrls.original;
  }

  get assetSrcOriginal() {
    return this.downloadUrls.original;
  }

  /*************************
   **  Tag accessors       **
   *************************
   *
   * `tags` is a free-form jsonb column. The categorized-upload feature
   * standardizes a handful of keys (see ATTACHMENT_FEEDBACK_REALTIME_VALIDATION).
   * Boolean tags use the existing `"true"`/`"false"` string convention so they
   * stay compatible with reviewer queries that use `tags @> '{...}'`.
   */

  /** What the employee declared the document is (set only by categorized upload). */
  get employeeSelectedType(): DocumentType | null {
    const v =
      this.tags?.['employeeSelectedType'] ??
      this.tags?.['employee_selected_type'] ??
      this.tags?.['employee-selected-type'];
    return typeof v === 'string' ? (v as DocumentType) : null;
  }

  /** Drives per-row UI state in the categorized upload component. */
  get aiValidationStatus(): AiValidationStatus | null {
    const v =
      this.tags?.['aiValidationStatus'] ??
      this.tags?.['ai_validation_status'] ??
      this.tags?.['ai-validation-status'];
    return typeof v === 'string' ? (v as AiValidationStatus) : null;
  }

  /** UI-specific validation state for cases where backend-compatible status is coarser. */
  get aiValidationDisplayStatus(): AiValidationDisplayStatus | null {
    const v =
      this.tags?.['aiValidationDisplayStatus'] ??
      this.tags?.['ai_validation_display_status'] ??
      this.tags?.['ai-validation-display-status'] ??
      this.aiValidationStatus;
    return typeof v === 'string' ? (v as AiValidationDisplayStatus) : null;
  }

  get aiValidationFeedback(): string | null {
    const v =
      this.tags?.['aiValidationFeedback'] ??
      this.tags?.['ai_validation_feedback'] ??
      this.tags?.['ai-validation-feedback'];
    return typeof v === 'string' ? v : null;
  }

  /** Every category Gemini found in the file (one upload may satisfy multiple slots). */
  get aiDetectedTypes(): DocumentType[] {
    const v =
      this.tags?.['aiDetectedTypes'] ??
      this.tags?.['ai_detected_types'] ??
      this.tags?.['ai-detected-types'];
    return Array.isArray(v) ? (v as DocumentType[]) : [];
  }

  /** Comma-joined detected types, falling back to "general" when empty. Used by translation
   * interpolation since `t` cannot accept array values. */
  get aiDetectedLabel(): string {
    const detected = this.aiDetectedTypes;
    return detected.length > 0 ? detected.join(', ') : 'general';
  }

  /** True iff the employee's selected type appears in the AI-detected list. */
  get aiTypeMatch(): boolean {
    return (
      (this.tags?.['aiTypeMatch'] ??
        this.tags?.['ai_type_match'] ??
        this.tags?.['ai-type-match']) === 'true'
    );
  }

  get aiIdentityMatch(): boolean {
    return (
      (this.tags?.['aiIdentityMatch'] ??
        this.tags?.['ai_identity_match'] ??
        this.tags?.['ai-identity-match']) === 'true'
    );
  }

  /** The row key that ties this asset to a specific categorized-upload row. */
  get uploadRowKey(): string | null {
    const v =
      this.tags?.['uploadRowKey'] ?? this.tags?.['upload_row_key'] ?? this.tags?.['upload-row-key'];
    return typeof v === 'string' ? v : null;
  }

  /**
   * Does this asset (per the AI's classification) satisfy the given required
   * document type? `grade` is treated as a subset of `transcript` so a full
   * transcript also fulfils a grade-only requirement.
   */
  satisfiesRequiredType(required: DocumentType): boolean {
    const detected = this.aiDetectedTypes;
    if (detected.includes(required)) return true;
    if (required === 'grade' && detected.includes('transcript')) return true;
    return false;
  }
}
